'use client'
import { useChannelChildrenInfinite } from '@/app/(site)/[username]/channel-children'
import { addGifsToChannel } from '@/app/util/channel-api'
import { EditContext } from '@/context/edit-manager'
import { FlashMessageContext } from '@/context/flash-message'
import UserContext from '@/context/user-context'
import { cn } from '@/util/cn'
import { IChannel } from '@giphy/js-types'
import { useContext } from 'react'
import Loader from '../../loader'
import { ModalContext } from '../../modal'
import AllGifsGrid from '../all-gifs-grid'
import AddToCollectionCallout from '../callouts'
import NewCollectionButton from '../new-collection-button'
import SearchInput from '../search-input'
import { cardClassName, ChannelContent, colorsClassName, MoreChildrenBadge, SelectedBadge } from '../ui'

type Props = {
    channelId?: IChannel['id']
    gifId?: string
    /**
     * Select mode, used by the creation tools' collection picker. The GIF does
     * not exist yet there, so a leaf click cannot "add" anything — it records a
     * choice the upload carries as `channel_id`. When this is passed, leaf
     * cards call it instead of posting via `AddGifToCollectionButton`, and the
     * modal stays open so several collections can be picked. Browsing,
     * drill-down, search and New Collection are identical in both modes.
     */
    onSelectChannel?: (channel: IChannel) => void
    /** Leaf cards whose id appears here render a `SelectedBadge`. */
    selectedChannelIds?: IChannel['id'][]
}

const peerClassName = 'group w-36'

export const AddGifToCollectionButton = ({
    className,
    children,
    gifId,
    channelId,
}: {
    className?: string
    children: React.ReactNode
    gifId?: string
    channelId: number
}) => {
    const { showStatusMessage } = useContext(FlashMessageContext)
    const { setModalType } = useContext(ModalContext)
    return (
        <div
            className={cn(className)}
            onClick={async () => {
                if (gifId) {
                    const result = await addGifsToChannel([gifId], channelId)
                    showStatusMessage('Added to collection', 'Failed to add to collection', result.ok)
                    setModalType(null)
                }
            }}
        >
            {children}
        </div>
    )
}

const AddToCollection = ({ channelId: channelId, gifId: id, onSelectChannel, selectedChannelIds }: Props) => {
    const { user } = useContext(UserContext)
    const isSubChannel = !!channelId
    const { setModalType, modal } = useContext(ModalContext)
    const { staffMode } = useContext(EditContext)

    const currentChannelId = channelId || modal?.channelId || user?.channel_id
    const { channel, isLoading, ref, children, doneFetching } = useChannelChildrenInfinite(currentChannelId!)
    if (!user) return null
    const rootChannel = channel?.id === user?.channel_id
    return (
        <div className="flex flex-col gap-2 p-2">
            {staffMode && (
                <SearchInput
                    onSelect={(channel) => {
                        setModalType({
                            name: 'addToCollection',
                            channelId: channel.id,
                            title: `Add to ${channel.display_name}`,
                        })
                    }}
                />
            )}
            <div
                className="flex animate-fade-in flex-wrap justify-start gap-4 overflow-x-hidden overflow-y-auto pt-2"
                style={{ maxHeight: 'calc(100vh - 300px)' }}
                key={`container-${isLoading}`}
            >
                {isLoading ? (
                    <Loader />
                ) : (
                    <>
                        {isSubChannel && rootChannel && modal?.name !== 'addToCollection' && (
                            <AddToCollectionCallout channel={channel} className={''} />
                        )}
                        {channel && <NewCollectionButton className={cn(peerClassName)} channel={channel} />}
                        {children?.map((subChannel) => {
                            const cClassName = cn(cardClassName, peerClassName, colorsClassName)
                            return subChannel.num_children === 0 ? (
                                onSelectChannel ? (
                                    <div
                                        key={subChannel.id}
                                        className={cClassName}
                                        onClick={() => onSelectChannel(subChannel)}
                                    >
                                        <ChannelContent channel={subChannel} />
                                        {selectedChannelIds?.includes(subChannel.id) && <SelectedBadge />}
                                    </div>
                                ) : (
                                    <AddGifToCollectionButton
                                        channelId={subChannel.id}
                                        gifId={id}
                                        className={cClassName}
                                        key={subChannel.id}
                                    >
                                        <ChannelContent channel={subChannel} />
                                    </AddGifToCollectionButton>
                                )
                            ) : (
                                <div
                                    key={subChannel.id}
                                    className={cClassName}
                                    onClick={() => {
                                        setModalType({
                                            name: 'addToCollection',
                                            channelId: subChannel.id,
                                            title: subChannel.display_name,
                                        })
                                    }}
                                >
                                    <ChannelContent channel={subChannel} />
                                    <MoreChildrenBadge count={subChannel.num_children} />
                                </div>
                            )
                        })}
                        {!doneFetching && (
                            <div ref={ref}>
                                <Loader />
                            </div>
                        )}
                    </>
                )}
            </div>
            {/*
             * Selecting the collection you are *inside*, rather than one of its
             * children. Without this a collection with sub-collections can only
             * be drilled into, never chosen — the cards above navigate on click
             * once `num_children > 0`.
             *
             * Legacy shows the same row (shared/components/collections/
             * add-to-collection/index.tsx:123-133), on the same condition: you
             * are in a collection that has a parent. `rootChannel` is that
             * check here — the user's own root channel is not a collection you
             * can add to.
             */}
            {onSelectChannel && channel && !rootChannel && (
                <button
                    type="button"
                    onClick={() => onSelectChannel(channel)}
                    className={cn(
                        'mt-2 flex w-full cursor-pointer items-center gap-2 rounded-md p-3 text-left text-giphyWhite',
                        selectedChannelIds?.includes(channel.id) ? 'bg-giphyAqua text-black' : 'bg-giphyDarkCharcoal'
                    )}
                >
                    {/* A literal '+', as the Tags field's add button uses
                        (create/gifmaker/finalize/fields/tags.tsx:114). There is
                        no `ss-plus` in packages/ui's glyph set, and an undefined
                        ss-* class renders nothing at all. */}
                    <span aria-hidden="true" className="shrink-0">
                        +
                    </span>
                    <span className="truncate">
                        Add to <b>{channel.display_name}</b>
                    </span>
                </button>
            )}
            {modal?.name !== 'addToCollection' && <AllGifsGrid channel={channel} />}
        </div>
    )
}
export default AddToCollection
